import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useSheetContainer } from "../hooks/useSheetContainer";
import { cn } from "../lib/utils";
import * as React from "react";
import { useEffect, useState } from "react";
const PopoverRoot = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;
const PopoverPortal = PopoverPrimitive.Portal;
const PopoverAnchor = PopoverPrimitive.Anchor;
const PopoverContent = React.forwardRef(({ className, align = "center", sideOffset = 4, mountPortal = true, mountPortalContainer, fullWidth = false, preventAutoFocusOnClose = true, onCloseAutoFocus, ...props }, ref) => {
    const handleCloseAutoFocus = React.useCallback((event) => {
        if (preventAutoFocusOnClose) {
            event.preventDefault();
        }
        onCloseAutoFocus === null || onCloseAutoFocus === void 0 ? void 0 : onCloseAutoFocus(event);
    }, [preventAutoFocusOnClose, onCloseAutoFocus]);
    const content = (React.createElement(PopoverPrimitive.Content, { ref: ref, align: align, sideOffset: sideOffset, className: cn("data-[state=open]:s-animate-in data-[state=open]:s-fade-in-0 data-[state=open]:s-zoom-in-95", "data-[state=closed]:s-animate-out data-[state=closed]:s-fade-out-0 data-[state=closed]:s-zoom-out-95", "data-[side=bottom]:s-slide-in-from-top-2", "data-[side=left]:s-slide-in-from-right-2", "data-[side=right]:s-slide-in-from-left-2", "data-[side=top]:s-slide-in-from-bottom-2", "s-z-50 s-rounded-xl s-border s-shadow-md s-outline-none", "s-border s-border-border dark:s-border-border-night", "s-bg-background dark:s-bg-background-night", "s-text-primary-950 dark:s-text-primary-950-night", fullWidth ? "s-grow" : "s-w-72 s-p-4", className), onCloseAutoFocus: handleCloseAutoFocus, ...props }));
    const container = useSheetContainer(mountPortalContainer);
    return mountPortal ? (React.createElement(PopoverPrimitive.Portal, { container: container }, content)) : (content);
});
function Popover({ trigger, popoverTriggerAsChild = false, content, ...props }) {
    return (React.createElement(PopoverRoot, null,
        React.createElement(PopoverTrigger, { asChild: popoverTriggerAsChild }, trigger),
        React.createElement(PopoverContent, { ...props }, content)));
}
PopoverContent.displayName = PopoverPrimitive.Content.displayName;
function AnchoredPopover({ open, anchorRef, children, className, ...props }) {
    const [position, setPosition] = useState({
        top: "50%",
        left: "50%",
        width: "0px",
        height: "0px",
    });
    useEffect(() => {
        if (!open) {
            return;
        }
        const updatePosition = () => {
            if (!(anchorRef === null || anchorRef === void 0 ? void 0 : anchorRef.current)) {
                setPosition({
                    top: "50%",
                    left: "50%",
                    width: "0px",
                    height: "0px",
                });
                return;
            }
            const rect = anchorRef.current.getBoundingClientRect();
            setPosition({
                top: `${rect.top}px`,
                left: `${rect.left}px`,
                width: `${rect.width}px`,
                height: `${rect.height}px`,
            });
        };
        updatePosition();
        const resizeObserver = new ResizeObserver(updatePosition);
        if (anchorRef === null || anchorRef === void 0 ? void 0 : anchorRef.current) {
            resizeObserver.observe(anchorRef.current);
        }
        window.addEventListener("scroll", updatePosition, true);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [open, anchorRef]);
    return (React.createElement(PopoverRoot, { open: open, modal: false },
        React.createElement(PopoverAnchor, { className: "s-fixed s-transition-all s-duration-300 s-ease-in-out", style: {
                top: position.top,
                left: position.left,
                width: position.width,
                height: position.height,
            } }),
        React.createElement(PopoverContent, { ...props, onOpenAutoFocus: (e) => e.preventDefault(), mountPortal: false, className: cn(className, !anchorRef && "s-translate-y-[-50%]") }, children)));
}
export { AnchoredPopover, Popover, PopoverAnchor, PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger, };
//# sourceMappingURL=Popover.js.map